import { useState, useEffect, useMemo } from 'react';
import { Page, Header, Content, ContentHeader, SupportButton, Table } from '@backstage/core-components';
import {
  EntityListProvider,
  useEntityList,
  EntityKindFilter,
  EntityRefLink,
} from '@backstage/plugin-catalog-react';
import { applicationPageTranslationRef } from '../../translation/application-page/application-page';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Grid, InputAdornment, MenuItem, TextField } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';

const PrefilteredTable = ({ kind = 'Application' }) => {
  const { updateFilters,  entities } = useEntityList();
  const { t } = useTranslationRef(applicationPageTranslationRef);

  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  // const [userGroups, setUserGroups] = useState<string[]>([]);

  const config = useApi(configApiRef);
  const backendUrl = config.getString('backend.baseUrl');

  const [applicationCodes, setApplicationCodes] = useState<any[]>([]);
  const [catalogEntities, setCatalogEntities] = useState<any[]>([]);
  useEffect(() => {
    const fetchGroups = async () => {
      const identity = await identityApi.getBackstageIdentity();      
      const userEntityRef = identity.userEntityRef;

      // Obtener email del usuario desde el catálogo
      let userEmail = userEntityRef.split('/')[1];
      try {
        const userEntity = await catalogApi.getEntityByRef(userEntityRef);
        const profile = userEntity?.spec?.profile as any;
        if (profile?.email) {
          userEmail = profile.email;
        }
      } catch (err) {
        console.warn('⚠️ Could not fetch user entity, using username instead:', err);
      }
      
      // Check for impersonated user (for development/testing)
      const impersonatedFromStorage = localStorage.getItem('backstage.impersonatedUser');
      const impersonatedFromConfig = config.getOptionalString('openfga.impersonatedUser');
      const impersonatedUser = impersonatedFromStorage || impersonatedFromConfig;
      
      let effectiveUser = impersonatedUser || userEmail;
      if (impersonatedUser && impersonatedUser !== userEmail) {
        try {
          const impersonatedUserRef = `user:default/${impersonatedUser}`;
          const impersonatedUserEntity = await catalogApi.getEntityByRef(impersonatedUserRef);
          const impersonatedProfile = impersonatedUserEntity?.spec?.profile as any;
          if (impersonatedProfile?.email) {
            effectiveUser = impersonatedProfile.email;
          }
        } catch (err) {
          console.warn('⚠️ Could not fetch impersonated user entity, using username instead:', err);
        }
        console.log('🎭 Impersonating user:', effectiveUser);
      }
      
      // Llama a OFGA para sacar las aplicaciones del usuario
      const token = (await identityApi.getCredentials()).token;
      const response = await fetch(`${backendUrl}/api/openfga-client/list-user-entities`, {
        method: 'POST',
        headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
            user: "user:" + effectiveUser,
            type: "application",
        }),
      });
      const data = await response.json();
      const applicationCodesFromFGA = (data.objects ?? [])
        .map((p: string) => {
          // Accept APM####### and legacy numeric ids (e.g. 1956)
          const match = p.match(/^application:(.+)$/i);
          return match ? match[1].toLowerCase() : null;
        })
        .filter(Boolean) as string[];
      console.log('Application codes from OpenFGA:', applicationCodesFromFGA);
      setApplicationCodes(applicationCodesFromFGA);
    };
    fetchGroups();
  }, [identityApi, catalogApi]);

  // Cargar todas las aplicaciones del catálogo
  useEffect(() => {
    const fetchApplications = async () => {
      const response = await catalogApi.getEntities({
        filter: { kind: 'Application' },
      });
      setCatalogEntities(response.items);
      console.log('📦 Loaded applications from catalog:', response.items.length);
    };
    fetchApplications();
  }, [catalogApi]);

  // Aplica los filtros una vez al montar
  useEffect(() => {
    updateFilters({
      kind: new EntityKindFilter(kind, kind),
    });
  }, [kind, updateFilters]);

  // Columnas personalizadas
  const customColumns = [
    //
    // --- Identificación del proyecto ---
    //
    {
      title: t('applicationTableColumns.apmNumber'),
      highlight: true,
      render: (row: any) => row.entity.spec?.number ?? '—',
    },
    {
      title: t('applicationTableColumns.name'),
      highlight: true,
      render: (row: any) => (
        <EntityRefLink
          entityRef={row.entity}
          defaultKind={row.entity.kind ?? 'Project'}
          defaultNamespace="default"
        />
      ),
    },
    {
      title: t('applicationTableColumns.division'),
      render: (row: any) => (
        row.entity.spec?.division ? <EntityRefLink
          entityRef={row.entity.spec?.division}
          defaultKind={'Division'}
          defaultNamespace="default"
        /> : '—'
      ),
    },
    {
      title: t('applicationTableColumns.category'),
      render: (row: any) => row.entity.spec?.category ?? '—',
    },
    {
      title: t('applicationTableColumns.manager'),
      render: (row: any) => (
        row.entity.spec?.manager ? <EntityRefLink
          entityRef={row.entity.spec?.manager}
          defaultKind={'User'}
          defaultNamespace="default"
        /> : '—'
      ),
    },
    {
      title: t('applicationTableColumns.itManager'),
      render: (row: any) => (
        row.entity.spec?.itManager ? <EntityRefLink
          entityRef={row.entity.spec?.itManager}
          defaultKind={'User'}
          defaultNamespace="default"
        /> : '—'
      ),
    },
    {
      title: t('applicationTableColumns.lifecycle'),
      render: (row: any) => row.entity.spec?.lifecycle ?? '—',
    },
    {
      title: t('applicationTableColumns.applicationServices'),
      render: (row: any) => {
        const services = row.entity.spec?.applicationServices as
          | Array<{ name: string }>
          | undefined;
        return services && services.length > 0
          ? services.map(s => s.name).join(', ')
          : '—';
      },
    },
  ];

  // Memoize filtered base entities to avoid changing reference on every render
  // Usar catalogEntities en lugar de entities del useEntityList si está disponible
  const allEntities = catalogEntities.length > 0 ? catalogEntities : entities;
  
  const filteredBaseEntities = useMemo(
    () => allEntities.filter(e => !e.spec?.parent),
    [allEntities]
  );
  
  // Filter by applicationCodes (case-insensitive)
  const authorizedApplications = useMemo(
    () => filteredBaseEntities.filter(e =>
      applicationCodes.map(code => code.toLowerCase()).includes(String(e.spec?.number ?? '').toLowerCase())
    ),
    [filteredBaseEntities, applicationCodes]
  );

  
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState('');

  const availableTypes = useMemo(() => {
    const types = authorizedApplications.map(e => e.spec?.category).filter(Boolean);
    return Array.from(new Set(types));
  }, [authorizedApplications]);

  const availableLifecycles = useMemo(() => {
    const lifecycles = authorizedApplications.map(e => e.spec?.lifecycle).filter(Boolean);
    return Array.from(new Set(lifecycles));
  }, [authorizedApplications]);

  const normalize = (text: string) =>
    text
      .toLowerCase()
      .normalize('NFD')                // separa acentos
      .replace(/[\u0300-\u036f]/g, '') // elimina acentos
      .replace(/\s+/g, ' ')           // colapsa espacios
      .trim();

  const filteredApplications = useMemo(() => {
    const normalizedSearch = normalize(search);

    return authorizedApplications.filter(app => {
      const name = normalize(app.metadata?.name ?? '');
      const title = normalize(app.metadata?.title ?? '');
      const number = normalize(String(app.spec?.number ?? ''));

      const matchesSearch = !normalizedSearch || [name, title, number].some(field =>
        normalizedSearch.split(' ').every(word => field.includes(word)),
      );

      const matchesType = !typeFilter || app.spec?.category === typeFilter;
      const matchesLifecycle = !lifecycleFilter || app.spec?.lifecycle === lifecycleFilter;

      return matchesSearch && matchesType && matchesLifecycle;
    });
  }, [authorizedApplications, search, typeFilter, lifecycleFilter]);

  // Debug logs
  useEffect(() => {
    console.log('📊 ApplicationPage Debug:', {
      entitiesFromUseEntityList: entities.length,
      catalogEntities: catalogEntities.length,
      allEntities: allEntities.length,
      filteredBase: filteredBaseEntities.length,
      applicationCodesFromFGA: applicationCodes.length,
      authorizedApplications: authorizedApplications.length,
      filteredApplications: filteredApplications.length,
    });
  }, [entities, catalogEntities, allEntities, filteredBaseEntities, applicationCodes, authorizedApplications, filteredApplications]);

  return (
    <div style={{ height: '100vh', width: '99%' }}>
      <Grid container direction="row" spacing={2} alignItems="center" justifyContent="flex-end" style={{ marginBottom: '16px' }}>
        <Grid item xs>
          <TextField
            select
            label={t('applicationPage.typeFilterLabel')}
            variant="outlined"
            size="small"
            value={typeFilter}
            fullWidth
            onChange={e => setTypeFilter(e.target.value)}
            style={{ minWidth: 160 }}
          >
            <MenuItem value="">{t('applicationPage.selectAll')}</MenuItem>
            {availableTypes.map((type: any) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs>
          <TextField
            select
            label={t('applicationPage.lifecycleFilterLabel')}
            variant="outlined"
            size="small"
            value={lifecycleFilter}
            fullWidth
            onChange={e => setLifecycleFilter(e.target.value)}
            style={{ minWidth: 160 }}
          >
            <MenuItem value="">{t('applicationPage.selectAll')}</MenuItem>
            {availableLifecycles.map((lifecycle: any) => (
              <MenuItem key={lifecycle} value={lifecycle}>
                {lifecycle}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item>
          <TextField
            label={t('applicationPage.searchPlaceholder')}
            variant="outlined"
            size="small"
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
      <Table
        title={t('applicationPage.title')}
        options={{ search: false, paging: true }}
        columns={customColumns}
        actions={[]}
        data={filteredApplications.map(e => ({ entity: e }))}
      />
    </div>
  );
};

export const ApplicationsPage = () => {
  const { t } = useTranslationRef(applicationPageTranslationRef);
  return (
    <Page themeId="other">
      <Header title={t('applicationPage.title')} subtitle={t('applicationPage.subtitle')} />
      <ContentHeader>
        <SupportButton>{t('applicationPage.supportButtonTitle')}</SupportButton>
      </ContentHeader>
      <Content noPadding>
        <EntityListProvider>
          <PrefilteredTable />
        </EntityListProvider>
      </Content>
    </Page>
  );
};